import { supabase } from '../config/supabase';
import { getApiBaseUrl } from '../utils/apiBase';

export interface SyncStatus {
  isDataFresh: boolean;
  lastSyncTime: Date | null;
  hoursAgo: number;
  needsSync: boolean;
}

export class SyncService {
  private static readonly SYNC_THRESHOLD_HOURS = 6; // Sync si han pasado más de 6 horas

  private static readonly UNIFIED_SYNC_TYPES = ['initial', 'incremental', 'manual', 'login'] as const;

  private static apiUrl(path: string): string {
    return `${getApiBaseUrl()}${path}`;
  }

  private static parseCompletedAt(completedAt: string | null | undefined): Date | null {
    if (!completedAt) return null;
    const hasTz =
      completedAt.endsWith('Z') ||
      completedAt.includes('+') ||
      completedAt.includes('-', 10);
    return new Date(hasTz ? completedAt : `${completedAt}Z`);
  }

  /**
   * Último log unificado exitoso (vía API — evita RLS en sync_logs desde el browser).
   */
  private static async fetchLastUnifiedSyncLog(
    storeId: string
  ): Promise<{ completed_at: string } | null> {
    const response = await fetch(this.apiUrl(`/api/sync/${storeId}?limit=50`), {
      method: 'GET',
    });
    if (!response.ok) {
      console.error('Error fetching sync logs:', response.status, response.statusText);
      return null;
    }

    const payload = (await response.json()) as { data?: Array<{ completed_at?: string; sync_type?: string; status?: string }> };
    const logs = payload.data ?? [];

    const unified = logs
      .filter(
        (log) =>
          log.status === 'success' &&
          log.completed_at &&
          this.UNIFIED_SYNC_TYPES.includes(log.sync_type as (typeof this.UNIFIED_SYNC_TYPES)[number])
      )
      .sort(
        (a, b) =>
          new Date(b.completed_at!).getTime() - new Date(a.completed_at!).getTime()
      );

    return unified[0]?.completed_at ? { completed_at: unified[0].completed_at } : null;
  }

  /**
   * Verificar si los datos necesitan sincronización
   */
  static async checkSyncStatus(storeId: string): Promise<SyncStatus> {
    try {
      let syncLog = await this.fetchLastUnifiedSyncLog(storeId);

      // Fallback: lectura directa (solo si RLS lo permite)
      if (!syncLog) {
        const { data, error } = await supabase
          .from('sync_logs')
          .select('completed_at, sync_type, status')
          .eq('store_id', storeId)
          .eq('status', 'success')
          .in('sync_type', [...this.UNIFIED_SYNC_TYPES])
          .order('completed_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          console.error('Error checking sync status:', error);
        } else if (data?.completed_at) {
          syncLog = { completed_at: data.completed_at };
        }
      }

      const completedAt = syncLog?.completed_at;
      const lastSyncTime = this.parseCompletedAt(completedAt);

      const hoursAgo = lastSyncTime
        ? (Date.now() - lastSyncTime.getTime()) / (1000 * 60 * 60)
        : 999;

      return {
        isDataFresh: hoursAgo < this.SYNC_THRESHOLD_HOURS,
        lastSyncTime,
        hoursAgo,
        needsSync: hoursAgo >= this.SYNC_THRESHOLD_HOURS
      };

    } catch (error) {
      console.error('Error in checkSyncStatus:', error);
      return {
        isDataFresh: false,
        lastSyncTime: null,
        hoursAgo: 999,
        needsSync: true
      };
    }
  }

  /**
   * Ejecutar sincronización (con continuación automática si hay lotes parciales)
   */
  static async triggerAutoSync(storeId: string): Promise<{ success: boolean; message: string }> {
    try {
      console.log(`🔄 Iniciando sync automático para tienda ${storeId}`);

      let resumeNextUrl: string | undefined;
      let batch = 0;
      const MAX_BATCHES = 100;
      let totalOrders = 0;

      do {
        batch++;
        const response = await fetch(this.apiUrl(`/api/sync/${storeId}`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'incremental',
            resumeNextUrl
          })
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok || !result.success) {
          const detail = result.error || response.statusText || 'Error desconocido';
          throw new Error(
            batch > 1
              ? `Error en lote ${batch} (tras ${totalOrders} órdenes): ${detail}`
              : `Error en sync unificado: ${detail}`
          );
        }

        totalOrders += result.data?.tiendanube?.recordsProcessed || 0;
        resumeNextUrl = result.partial && result.hasMore ? result.nextPageUrl : undefined;

        if (resumeNextUrl) {
          console.log(`📦 Lote ${batch} completado (${totalOrders} órdenes). Continuando catch-up...`);
        }
      } while (resumeNextUrl && batch < MAX_BATCHES);

      console.log(`✅ Sync automático completado para tienda ${storeId} (${batch} lotes, ${totalOrders} órdenes)`);
      return {
        success: true,
        message: batch > 1
          ? `Datos actualizados (${batch} lotes, ${totalOrders} órdenes)`
          : 'Datos actualizados correctamente'
      };

    } catch (error: any) {
      console.error('Error en triggerAutoSync:', error);
      return {
        success: false,
        message: error.message || 'Error actualizando datos'
      };
    }
  }

  /**
   * Sync inteligente: solo si es necesario
   */
  static async smartSync(storeId: string): Promise<{ wasNeeded: boolean; result?: any }> {
    const status = await this.checkSyncStatus(storeId);
    
    if (status.needsSync) {
      console.log(`📊 Datos antiguos (${status.hoursAgo.toFixed(1)}h). Iniciando sync...`);
      const result = await this.triggerAutoSync(storeId);
      return { wasNeeded: true, result };
    } else {
      console.log(`✅ Datos frescos (${status.hoursAgo.toFixed(1)}h). No es necesario sync.`);
      return { wasNeeded: false };
    }
  }

  /**
   * Obtener logs de sincronización
   */
  static async getSyncStats(storeId: string) {
    try {
      const response = await fetch(this.apiUrl(`/api/sync/logs/${storeId}`));
      if (!response.ok) {
        throw new Error('Error obteniendo logs de sincronización');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting sync stats:', error);
      return null;
    }
  }
}
